import React, { useState, useEffect, useRef } from 'react';

import family3bed from '../assets/family3bed.png';
import kingsize from '../assets/kingsize.png';
import mandalafront from '../assets/mandalafront-king.png';
import lify from '../assets/lify.png';
import lobby from '../assets/lobby.png';

const photos = [
  {
    src: mandalafront,
    alt: 'King Suite',
    span: 'tall',
  },
  {
    src: kingsize,
    alt: 'King Deluxe',
    span: '',
  },
  {
    src: family3bed,
    alt: 'Family Suite',
    span: '',
  },
  {
    src: lify,
    alt: 'Hotel Lift Area',
    span: '',
  },
  {
    src: lobby,
    alt: 'Hotel Lobby',
    span: '',
  },
];

export default function Gallery() {
  const [light, setLight] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;

    ref.current.style.opacity = '0';

    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          e.target.style.transition = 'opacity .9s ease';
          e.target.style.opacity = '1';
        }
      },
      { threshold: 0.08 }
    );

    obs.observe(ref.current);

    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') setLight(null);
    };

    window.addEventListener('keydown', h);

    return () => window.removeEventListener('keydown', h);
  }, []);

  const ImageFallback = ({ src, alt }) => {
    const [error, setError] = useState(false);

    if (error || !src) {
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            minHeight: '220px',
            background: '#E8D9C4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#1B2F6E',
            fontFamily: "'Jost', sans-serif",
            letterSpacing: '2px',
            fontSize: '.8rem',
            textTransform: 'uppercase',
          }}
        >
          Place image here
        </div>
      );
    }

    return (
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onError={() => setError(true)}
      />
    );
  };

  return (
    <>
      <style>{`
        .gal-thumb {
          overflow:hidden;
          cursor:pointer;
          position:relative;
        }

        .gal-thumb img {
          width:100%;
          height:100%;
          object-fit:cover;
          display:block;
          transition:transform .6s ease;
        }

        .gal-thumb:hover img {
          transform:scale(1.07);
        }

        .gal-thumb-overlay {
          position:absolute;
          inset:0;
          background:rgba(27,47,110,0);
          transition:background .35s;
          display:flex;
          align-items:center;
          justify-content:center;
        }

        .gal-thumb:hover .gal-thumb-overlay {
          background:rgba(27,47,110,0.35);
        }

        .gal-plus {
          opacity:0;
          color:#C9A84C;
          font-size:2rem;
          transition:opacity .35s;
        }

        .gal-thumb:hover .gal-plus {
          opacity:1;
        }

        .lightbox {
          position:fixed;
          inset:0;
          background:rgba(10,15,38,0.96);
          z-index:500;
          display:flex;
          align-items:center;
          justify-content:center;
          animation:fadeIn .3s ease;
          cursor:zoom-out;
        }

        @keyframes fadeIn {
          from { opacity:0 }
          to { opacity:1 }
        }

        .lightbox img {
          max-width:90vw;
          max-height:88vh;
          object-fit:contain;
          box-shadow:0 24px 80px rgba(0,0,0,.6);
          border:1px solid rgba(201,168,76,.25);
        }

        .lb-close {
          position:absolute;
          top:1.5rem;
          right:1.5rem;
          color:#C9A84C;
          font-size:1.6rem;
          cursor:pointer;
          background:none;
          border:none;
          font-family:'Jost',sans-serif;
          opacity:.8;
          transition:opacity .3s;
        }

        .lb-close:hover {
          opacity:1;
        }

        @media(max-width:860px){
          .gal-grid {
            grid-template-columns:1fr 1fr !important;
          }

          .gal-tall {
            grid-row:auto !important;
          }
        }
      `}</style>

      <section
        id="gallery"
        style={{
          padding: '7rem 3rem',
          background: '#F2E8D9',
        }}
      >
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '.8rem',
                justifyContent: 'center',
                marginBottom: '1rem',
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '1px',
                  background: '#C9A84C',
                }}
              />
              <div
                style={{
                  width: '5px',
                  height: '5px',
                  background: '#C9A84C',
                  transform: 'rotate(45deg)',
                }}
              />
              <div
                style={{
                  width: '32px',
                  height: '1px',
                  background: '#C9A84C',
                }}
              />
            </div>

            <p
              style={{
                fontFamily: "'Jost',sans-serif",
                fontSize: '.58rem',
                letterSpacing: '5px',
                textTransform: 'uppercase',
                color: '#1B2F6E',
                marginBottom: '.7rem',
                fontWeight: 400,
              }}
            >
              Photo Gallery
            </p>

            <h2
              style={{
                fontFamily: "'Cinzel',serif",
                fontSize: 'clamp(1.9rem,4vw,2.9rem)',
                fontWeight: 500,
                color: '#1B2F6E',
                marginBottom: '.2rem',
              }}
            >
              A Glimpse
            </h2>

            <h3
              style={{
                fontFamily: "'Cormorant',serif",
                fontSize: 'clamp(1.2rem,2.5vw,1.9rem)',
                fontStyle: 'italic',
                fontWeight: 400,
                color: '#C9A84C',
              }}
            >
              Inside Shree Maya
            </h3>
          </div>

          <div
            ref={ref}
            className="gal-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr',
              gridTemplateRows: 'auto auto',
              gap: '6px',
            }}
          >
            {photos.map((p, i) => (
              <div
                key={i}
                className={`gal-thumb${i === 0 ? ' gal-tall' : ''}`}
                style={{
                  gridRow: i === 0 ? 'span 2' : 'auto',
                  aspectRatio: i === 0 ? 'unset' : '4/3',
                  height: i === 0 ? '100%' : 'unset',
                }}
                onClick={() => p.src && setLight(p.src)}
              >
                <ImageFallback src={p.src} alt={p.alt} />

                <div className="gal-thumb-overlay">
                  <span className="gal-plus">+</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {light && (
        <div className="lightbox" onClick={() => setLight(null)}>
          <button className="lb-close" onClick={() => setLight(null)}>
            ✕
          </button>

          <img
            src={light}
            alt="Gallery"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}